import {useCallback, useRef, useState} from 'react';
import {toPng} from 'html-to-image';
import {useTheme} from '@mui/material';

import {useHostName} from './useHostName';

const filter = (node: HTMLElement) => {
	const exclusionClasses = ['remove-me'];
	return !exclusionClasses.some(classname => node.classList?.contains(classname));
};

export function usePrint(fileName = 'comprovante') {
	const [isPrinting, setIsPrinting] = useState(false);
	const [isSharing, setIsSharing] = useState(false);
	const [printError, setPrintError] = useState(false);
	const printRef = useRef<HTMLDivElement>(null);
	const {dataHostNameState} = useHostName();
	const theme = useTheme();

	const printContent = useCallback(async () => {
		if (printRef.current === null) {
			return;
		}

		try {
			setIsPrinting(true);
			const dataUrl = await toPng(printRef.current, {
				cacheBust: true,
				filter,
				style: {
					backgroundColor: theme.palette.grey[200],
					opacity: '1',
				},
			});
			const link = document.createElement('a');
			link.download = `${fileName}-${dataHostNameState?.product}`;
			link.href = dataUrl;
			link.click();
		} catch (error) {
			setPrintError(true);
		} finally {
			setIsPrinting(false);
		}
	}, [printRef, dataHostNameState, theme]);

	const shareContent = useCallback(
		async (onLinkCopied?: () => void) => {
			if (printRef.current === null) {
				return;
			}

			try {
				setIsSharing(true);
				const dataUrl = await toPng(printRef.current, {
					cacheBust: true,
					filter,
					style: {
						backgroundColor: theme.palette.grey[200],
						opacity: '1',
					},
				});

				const responseBlob = await fetch(dataUrl);
				const blob = await responseBlob.blob();

				const hash = Array.from(crypto.getRandomValues(new Uint8Array(3)), byte =>
					byte.toString(16).padStart(2, '0'),
				).join('');
				const fileNameWithHash = `${fileName}-${dataHostNameState?.product}_${hash}.png`;
				const file = new File([blob], fileNameWithHash, {type: 'image/png'});

				if (navigator.canShare && navigator.canShare({files: [file]})) {
					try {
						await navigator.share({
							files: [file],
						});
					} catch {
						return;
					}
				} else {
					await navigator.clipboard.writeText(dataUrl);
					onLinkCopied?.();
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error(error);
				setPrintError(true);
			} finally {
				setIsSharing(false);
			}
		},
		[printRef, dataHostNameState, fileName, theme],
	);

	return {printContent, shareContent, isPrinting, isSharing, printRef, printError, setPrintError};
}
